import { useState, useEffect, useCallback } from "react";
import { useLocation } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StepProgress } from "@/components/funnels/StepProgress";
import { OptionCard } from "@/components/funnels/OptionCard";
import { BodyDiagram } from "@/components/funnels/BodyDiagram";
import { BaselineInputs } from "@/components/funnels/BaselineInputs";
import { SummaryCard } from "@/components/funnels/SummaryCard";
import { PortalPreview } from "@/components/funnels/PortalPreview";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Zap, Stethoscope, Activity, HelpCircle, ArrowLeft, User } from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────
type Path = "orthopedic" | "gi" | "other" | null;
type DiagramMode = "male" | "female" | "list" | null;
type ConcernKey = "recent-injury" | "surgery-recovery" | "gi" | "other" | null;

interface FunnelState {
  step: number;
  path: Path;
  concernKey: ConcernKey;
  diagramMode: DiagramMode;
  selectedRegions: string[];
  regionAnswers: Record<string, Record<string, string>>;
  giAnswers: {
    symptoms: string[];
    frequency: string;
    duration: string;
    triggers: string;
  };
  otherConcern: string;
  baseline: {
    painLevel: number;
    mobilityRating: number;
    duration: string;
    dailyImpact: string;
    giFrequency: string;
    giSeverity: number;
    giDuration: string;
  };
  confirmed: boolean;
}

const STORAGE_KEY = "funnel-bpc157-state";

const initialState: FunnelState = {
  step: 0,
  path: null,
  concernKey: null,
  diagramMode: null,
  selectedRegions: [],
  regionAnswers: {},
  giAnswers: { symptoms: [], frequency: "", duration: "", triggers: "" },
  otherConcern: "",
  baseline: {
    painLevel: 0,
    mobilityRating: 3,
    duration: "",
    dailyImpact: "",
    giFrequency: "",
    giSeverity: 0,
    giDuration: "",
  },
  confirmed: false,
};

// ─── Step sequences (internal step numbers per path, excluding step 0) ────────
//   orthopedic: Concern(1) → AnatRef(2) → BodyRegion(3) → SymptomDetails(4)
//               → Baseline(6) → Summary(7) → Confirm(8) → Portal(9)
//   gi:         Concern(1) → GI/Other(5) → Baseline(6) → Summary(7) → Confirm(8) → Portal(9)
//   other:      Concern(1) → GI/Other(5) → Summary(7) → Confirm(8) → Portal(9)
const PATH_SEQUENCES: Record<NonNullable<Path>, number[]> = {
  orthopedic: [1, 2, 3, 4, 6, 7, 8, 9],
  gi:         [1, 5, 6, 7, 8, 9],
  other:      [1, 5, 7, 8, 9],
};

// Display-step labels (portal step 9 omitted — progress bar is hidden on step 9)
const PATH_STEP_LABELS: Record<NonNullable<Path>, string[]> = {
  orthopedic: ["Concern Type", "Anatomical Reference", "Body Region", "Symptom Details", "Baseline", "Summary", "Confirm"],
  gi:         ["Concern Type", "GI Concerns", "Baseline", "Summary", "Confirm"],
  other:      ["Concern Type", "Other Concern", "Summary", "Confirm"],
};

function getDisplayStepInfo(internalStep: number, path: Path) {
  // Before path is chosen (step 1), show a generic placeholder
  if (!path || internalStep === 1) {
    return { displayStep: 1, totalSteps: 7, stepLabel: "Concern Type" };
  }
  const seq = PATH_SEQUENCES[path].filter((s) => s !== 9); // exclude portal from count
  const labels = PATH_STEP_LABELS[path];
  const idx = seq.indexOf(internalStep);
  if (idx < 0) return { displayStep: 1, totalSteps: seq.length, stepLabel: "" };
  return { displayStep: idx + 1, totalSteps: seq.length, stepLabel: labels[idx] ?? "" };
}

// ─── Region follow-up questions ───────────────────────────────────────────────
const regionQuestions: Record<string, string[]> = {
  "head-neck":   ["Is there headache accompanying the pain?", "Do you have neck stiffness?"],
  "shoulder":    ["Is your range of motion limited?", "Do you have pain at night?", "Does the pain radiate down your arm?"],
  "upper-arm":   ["Is there visible bruising or swelling?", "Does raising the arm above shoulder height worsen it?"],
  "elbow":       ["Is it a tennis elbow or golfer's elbow pattern?", "Any grip weakness?"],
  "forearm":     ["Is there numbness or tingling in the hand?", "Is the pain sharp or aching?"],
  "wrist-hand":  ["Do you have numbness or tingling?", "Is the pain worse at night?"],
  "chest-ribs":  ["Is this musculoskeletal chest pain (not cardiac)?", "Do you have rib tenderness?"],
  "abdomen":     ["Is the pain localized or diffuse?", "Is it related to eating?"],
  "upper-back":  ["Do you have muscle spasms?", "Is the pain related to posture?"],
  "lower-back":  ["Is the pain constant or intermittent?", "Do you have leg numbness or tingling?", "Is the pain worse sitting or standing?"],
  "hips-pelvis": ["Does it hurt to bear weight?", "Is there clicking or popping?"],
  "upper-leg":   ["Is the pain in the front (quad) or back (hamstring) of the thigh?", "Does it worsen going up stairs?"],
  "knee":        ["Do you experience swelling?", "Do you have morning stiffness?", "What triggers sharp pain?"],
  "lower-leg":   ["Is there calf tightness or cramping?", "Any swelling below the knee?"],
  "ankle-foot":  ["Is there visible swelling?", "Does weight-bearing worsen it?"],
};

const giSymptoms = ["Bloating", "Cramping", "Irregular bowel movements", "Nausea", "Acid reflux", "Abdominal pain"];

// ─── Animation variants ───────────────────────────────────────────────────────
const variants = {
  enter:  (d: number) => ({ x: d > 0 ?  50 : -50, opacity: 0 }),
  center:              ({ x: 0,                     opacity: 1 }),
  exit:   (d: number) => ({ x: d < 0 ?  50 : -50, opacity: 0 }),
};
const transition = { duration: 0.3 };

// ─── Component ────────────────────────────────────────────────────────────────
export default function BPC157Funnel() {
  const [, setLocation] = useLocation();
  const [state, setState] = useState<FunnelState>(initialState);

  // Local UI state for "use a list instead" toggle on the Body Region step —
  // lives here (not in global state) so it resets correctly on back/forward.
  const [bodyRegionShowList, setBodyRegionShowList] = useState(false);

  // Reset the list-override whenever we arrive at step 3
  useEffect(() => {
    if (state.step === 3) {
      setBodyRegionShowList(state.diagramMode === "list");
    }
  }, [state.step]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Persistence ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try { setState(JSON.parse(saved)); }
      catch (e) { console.error("Failed to parse saved state", e); }
    }
  }, []);

  useEffect(() => {
    if (state.step > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }
  }, [state]);

  // ── Browser back button ──────────────────────────────────────────────────────
  useEffect(() => {
    const handlePopState = () => {
      if (state.step > 0) setState((prev) => ({ ...prev, step: prev.step - 1 }));
      else setLocation("/funnels");
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [state.step, setLocation]);

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const startOver = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setState(initialState);
  }, []);

  const goToStep = useCallback((step: number) => {
    setState((prev) => ({ ...prev, step }));
  }, []);

  // Path-aware back navigation — skips steps that don't render for the current path
  const getPrevStep = useCallback((currentStep: number, path: Path): number => {
    const seq = path === "gi" ? [0, 1, 5, 6, 7, 8, 9]
              : path === "other" ? [0, 1, 5, 7, 8, 9]
              : [0, 1, 2, 3, 4, 6, 7, 8, 9]; // orthopedic + null
    const idx = seq.indexOf(currentStep);
    if (idx > 0) return seq[idx - 1];
    return Math.max(0, currentStep - 1);
  }, []);

  const goBack = useCallback(() => {
    if (state.step > 0) {
      setState((prev) => ({ ...prev, step: getPrevStep(prev.step, prev.path) }));
    }
  }, [state.step, getPrevStep]);

  const handleRegionToggle = useCallback((regionId: string) => {
    setState((prev) => {
      const sel = prev.selectedRegions.includes(regionId);
      return {
        ...prev,
        selectedRegions: sel
          ? prev.selectedRegions.filter((r) => r !== regionId)
          : [...prev.selectedRegions, regionId],
      };
    });
  }, []);

  const handleRegionAnswer = useCallback((regionId: string, question: string, answer: string) => {
    setState((prev) => ({
      ...prev,
      regionAnswers: {
        ...prev.regionAnswers,
        [regionId]: { ...(prev.regionAnswers[regionId] || {}), [question]: answer },
      },
    }));
  }, []);

  const handleGISymptomToggle = useCallback((symptom: string) => {
    setState((prev) => {
      const sel = prev.giAnswers.symptoms.includes(symptom);
      return {
        ...prev,
        giAnswers: {
          ...prev.giAnswers,
          symptoms: sel
            ? prev.giAnswers.symptoms.filter((s) => s !== symptom)
            : [...prev.giAnswers.symptoms, symptom],
        },
      };
    });
  }, []);

  const handleBaselineChange = useCallback((key: string, value: string | number) => {
    setState((prev) => ({ ...prev, baseline: { ...prev.baseline, [key]: value } }));
  }, []);

  const calculateBaselineScore = (): number => {
    if (state.path === "orthopedic") {
      const painScore = (state.baseline.painLevel / 10) * 40;
      const mobilityScore = ((6 - state.baseline.mobilityRating) / 5) * 30;
      const impactMap: Record<string, number> = { none: 0, mild: 10, moderate: 20, severe: 30 };
      return Math.round(painScore + mobilityScore + (impactMap[state.baseline.dailyImpact] || 0));
    } else if (state.path === "gi") {
      const freqMap: Record<string, number> = { occasionally: 0, weekly: 10, "several-times-week": 20, daily: 30 };
      return Math.round((state.baseline.giSeverity / 10) * 70 + (freqMap[state.baseline.giFrequency] || 0));
    }
    return 0;
  };

  // ── Step renderer ─────────────────────────────────────────────────────────────
  const renderStep = () => {
    switch (state.step) {
      // ── 0 · Intro ─────────────────────────────────────────────────────────────
      case 0:
        return (
          <motion.div
            key="step-0"
            variants={variants} custom={1}
            initial="enter" animate="center" exit="exit"
            transition={transition}
            className="max-w-3xl mx-auto space-y-8"
          >
            <div className="text-center space-y-6">
              <h1 className="text-4xl md:text-5xl font-serif font-medium">
                BPC-157 Recovery Journey
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
                This is an interactive intake prototype for a BPC-157 peptide recovery program.
                It demonstrates an outcome-oriented patient experience across orthopedic and
                digestive health pathways.
              </p>

              {state.step === 0 && localStorage.getItem(STORAGE_KEY) && (
                <div className="p-4 bg-muted/50 rounded-lg border border-border">
                  <p className="text-sm font-medium mb-3">You have a saved session</p>
                  <div className="flex gap-3 justify-center">
                    <Button
                      onClick={() => {
                        const saved = localStorage.getItem(STORAGE_KEY);
                        if (saved) setState(JSON.parse(saved));
                      }}
                      className="bg-teal-700 hover:bg-teal-800 text-white border-teal-800"
                    >
                      Resume where you left off
                    </Button>
                    <Button onClick={startOver} variant="outline">Start fresh</Button>
                  </div>
                </div>
              )}

              <Button
                onClick={() => goToStep(1)}
                size="lg"
                className="bg-teal-700 hover:bg-teal-800 text-white border-teal-800 rounded-full px-8"
                data-testid="button-begin-funnel"
              >
                Begin Intake
              </Button>
            </div>
          </motion.div>
        );

      // ── 1 · Concern Type ──────────────────────────────────────────────────────
      case 1:
        return (
          <motion.div
            key="step-1"
            variants={variants} custom={1}
            initial="enter" animate="center" exit="exit"
            transition={transition}
            className="max-w-2xl mx-auto space-y-8"
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-serif">What brings you in today?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <OptionCard
                  icon={<Zap className="w-6 h-6" />}
                  label="Recent injury"
                  selected={state.concernKey === "recent-injury"}
                  onClick={() => setState((prev) => ({ ...prev, path: "orthopedic", concernKey: "recent-injury", step: 2 }))}
                  testId="option-concern-recent-injury"
                />
                <OptionCard
                  icon={<Stethoscope className="w-6 h-6" />}
                  label="Surgery recovery"
                  selected={state.concernKey === "surgery-recovery"}
                  onClick={() => setState((prev) => ({ ...prev, path: "orthopedic", concernKey: "surgery-recovery", step: 2 }))}
                  testId="option-concern-surgery-recovery"
                />
                <OptionCard
                  icon={<Activity className="w-6 h-6" />}
                  label="Digestive or GI concern"
                  selected={state.concernKey === "gi"}
                  onClick={() => setState((prev) => ({ ...prev, path: "gi", concernKey: "gi", step: 5 }))}
                  testId="option-concern-digestive-gi"
                />
                <OptionCard
                  icon={<HelpCircle className="w-6 h-6" />}
                  label="Other concern"
                  selected={state.concernKey === "other"}
                  onClick={() => setState((prev) => ({ ...prev, path: "other", concernKey: "other", step: 5 }))}
                  testId="option-concern-other"
                />
              </CardContent>
            </Card>
          </motion.div>
        );

      // ── 2 · Anatomical Reference (orthopedic only, NEW) ───────────────────────
      case 2:
        return (
          <motion.div
            key="step-2"
            variants={variants} custom={1}
            initial="enter" animate="center" exit="exit"
            transition={transition}
            className="max-w-2xl mx-auto space-y-8"
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-serif">
                  Which body diagram would you like to use?
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  We'll use this to help you mark the affected area. You can switch to a
                  list view at any time on the next step.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <OptionCard
                  icon={<User className="w-6 h-6" />}
                  label="Male diagram"
                  sublabel="Select regions on a male anatomical reference"
                  selected={state.diagramMode === "male"}
                  onClick={() => setState((prev) => ({ ...prev, diagramMode: "male", step: 3 }))}
                  testId="option-diagram-mode-male"
                />
                <OptionCard
                  icon={<User className="w-6 h-6" />}
                  label="Female diagram"
                  sublabel="Select regions on a female anatomical reference"
                  selected={state.diagramMode === "female"}
                  onClick={() => setState((prev) => ({ ...prev, diagramMode: "female", step: 3 }))}
                  testId="option-diagram-mode-female"
                />
                <OptionCard
                  icon={<HelpCircle className="w-6 h-6" />}
                  label="Prefer not to say"
                  sublabel="Use a numbered list to select affected regions instead"
                  selected={state.diagramMode === "list"}
                  onClick={() => setState((prev) => ({ ...prev, diagramMode: "list", step: 3 }))}
                  testId="option-diagram-mode-prefer-not"
                />

                <div className="pt-2">
                  <Button onClick={goBack} variant="outline">
                    <ArrowLeft className="mr-2 w-4 h-4" />
                    Back
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        );

      // ── 3 · Body Region (orthopedic only) ─────────────────────────────────────
      case 3: {
        // bodyRegionShowList is driven by the useEffect above; no gender buttons here.
        const inListMode = bodyRegionShowList;
        return (
          <motion.div
            key="step-3"
            variants={variants} custom={1}
            initial="enter" animate="center" exit="exit"
            transition={transition}
            className="max-w-2xl mx-auto space-y-8"
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-serif">
                  Where is your concern located?
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  {inListMode
                    ? "Check all regions that apply."
                    : "Tap a region on the diagram, or check the list on the right."}
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                {inListMode ? (
                  <BodyDiagram
                    mode="list"
                    selectedRegions={state.selectedRegions}
                    onRegionToggle={handleRegionToggle}
                  />
                ) : (
                  <>
                    <BodyDiagram
                      sex={(state.diagramMode as "male" | "female") ?? "male"}
                      mode="diagram"
                      selectedRegions={state.selectedRegions}
                      onRegionToggle={handleRegionToggle}
                    />
                    <button
                      type="button"
                      onClick={() => setBodyRegionShowList(true)}
                      className="text-sm text-teal-700 hover:underline"
                      data-testid="button-use-list-instead"
                    >
                      Use a list instead
                    </button>
                  </>
                )}

                <div className="flex gap-3 pt-2">
                  <Button onClick={goBack} variant="outline">
                    <ArrowLeft className="mr-2 w-4 h-4" />
                    Back
                  </Button>
                  <Button
                    onClick={() => goToStep(4)}
                    disabled={state.selectedRegions.length === 0}
                    className="flex-1 bg-teal-700 hover:bg-teal-800 text-white border-teal-800"
                    data-testid="button-continue-body-region"
                  >
                    Continue
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        );
      }

      // ── 4 · Symptom Details (orthopedic only) ─────────────────────────────────
      case 4:
        return (
          <motion.div
            key="step-4"
            variants={variants} custom={1}
            initial="enter" animate="center" exit="exit"
            transition={transition}
            className="max-w-2xl mx-auto space-y-8"
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-serif">Tell us more</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {state.selectedRegions.map((regionId) => {
                  const questions = regionQuestions[regionId] || [
                    "How severe is the pain on a scale of 1–10?",
                    "Is it getting better or worse over time?",
                  ];
                  return (
                    <div
                      key={regionId}
                      className="p-4 bg-muted/50 rounded-lg space-y-4"
                      data-testid={`question-${regionId}-0`}
                    >
                      <h4 className="font-semibold text-teal-700 capitalize">
                        {regionId.replace(/-/g, " ")}
                      </h4>
                      {questions.map((question, idx) => (
                        <div key={idx} className="space-y-2">
                          <Label className="text-sm">{question}</Label>
                          <Input
                            placeholder="Your answer"
                            value={state.regionAnswers[regionId]?.[question] || ""}
                            onChange={(e) => handleRegionAnswer(regionId, question, e.target.value)}
                          />
                        </div>
                      ))}
                    </div>
                  );
                })}

                <div className="flex gap-3 pt-4">
                  <Button onClick={goBack} variant="outline">
                    <ArrowLeft className="mr-2 w-4 h-4" />
                    Back
                  </Button>
                  <Button
                    onClick={() => goToStep(6)}
                    className="flex-1 bg-teal-700 hover:bg-teal-800 text-white border-teal-800"
                  >
                    Continue
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        );

      // ── 5 · GI / Other Details ────────────────────────────────────────────────
      case 5:
        if (state.path === "gi") {
          return (
            <motion.div
              key="step-5-gi"
              variants={variants} custom={1}
              initial="enter" animate="center" exit="exit"
              transition={transition}
              className="max-w-2xl mx-auto space-y-8"
            >
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl font-serif">
                    Tell us about your digestive concerns
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-3">
                    <Label className="text-base font-medium">Symptoms (select all that apply)</Label>
                    <div className="grid grid-cols-2 gap-3">
                      {giSymptoms.map((symptom) => (
                        <div
                          key={symptom}
                          className="flex items-center space-x-2"
                          data-testid={`checkbox-gi-symptom-${symptom.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          <Checkbox
                            id={symptom}
                            checked={state.giAnswers.symptoms.includes(symptom)}
                            onCheckedChange={() => handleGISymptomToggle(symptom)}
                          />
                          <Label htmlFor={symptom} className="cursor-pointer">{symptom}</Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label htmlFor="gi-freq" className="text-base font-medium">Frequency</Label>
                    <Select
                      value={state.giAnswers.frequency}
                      onValueChange={(val) =>
                        setState((prev) => ({ ...prev, giAnswers: { ...prev.giAnswers, frequency: val } }))
                      }
                    >
                      <SelectTrigger id="gi-freq" data-testid="select-gi-frequency">
                        <SelectValue placeholder="Select frequency" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="several-times-week">Several times a week</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="occasionally">Occasionally</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-3">
                    <Label htmlFor="gi-duration" className="text-base font-medium">Duration</Label>
                    <Select
                      value={state.giAnswers.duration}
                      onValueChange={(val) =>
                        setState((prev) => ({ ...prev, giAnswers: { ...prev.giAnswers, duration: val } }))
                      }
                    >
                      <SelectTrigger id="gi-duration" data-testid="select-gi-duration">
                        <SelectValue placeholder="Select duration" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="less-2-weeks">Less than 2 weeks</SelectItem>
                        <SelectItem value="2-4-weeks">2–4 weeks</SelectItem>
                        <SelectItem value="1-3-months">1–3 months</SelectItem>
                        <SelectItem value="3-plus-months">More than 3 months</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-3">
                    <Label htmlFor="gi-triggers" className="text-base font-medium">
                      Known triggers (optional)
                    </Label>
                    <Input
                      id="gi-triggers"
                      placeholder="Any known foods or activities that worsen symptoms?"
                      value={state.giAnswers.triggers}
                      onChange={(e) =>
                        setState((prev) => ({ ...prev, giAnswers: { ...prev.giAnswers, triggers: e.target.value } }))
                      }
                      data-testid="input-gi-triggers"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button onClick={goBack} variant="outline">
                      <ArrowLeft className="mr-2 w-4 h-4" />
                      Back
                    </Button>
                    <Button
                      onClick={() => goToStep(6)}
                      disabled={state.giAnswers.symptoms.length === 0}
                      className="flex-1 bg-teal-700 hover:bg-teal-800 text-white border-teal-800"
                    >
                      Continue
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        } else if (state.path === "other") {
          return (
            <motion.div
              key="step-5-other"
              variants={variants} custom={1}
              initial="enter" animate="center" exit="exit"
              transition={transition}
              className="max-w-2xl mx-auto space-y-8"
            >
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl font-serif">Tell us more about your concern</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-3">
                    <Label htmlFor="other-concern" className="text-base font-medium">
                      Describe your concern in your own words
                    </Label>
                    <Textarea
                      id="other-concern"
                      rows={6}
                      placeholder="Tell us what brings you here today..."
                      value={state.otherConcern}
                      onChange={(e) => setState((prev) => ({ ...prev, otherConcern: e.target.value }))}
                      data-testid="textarea-other-concern"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button onClick={goBack} variant="outline">
                      <ArrowLeft className="mr-2 w-4 h-4" />
                      Back
                    </Button>
                    <Button
                      onClick={() => goToStep(7)}
                      disabled={state.otherConcern.length < 10}
                      className="flex-1 bg-teal-700 hover:bg-teal-800 text-white border-teal-800"
                    >
                      Continue
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        }
        return null;

      // ── 6 · Baseline ──────────────────────────────────────────────────────────
      case 6:
        return (
          <motion.div
            key="step-6"
            variants={variants} custom={1}
            initial="enter" animate="center" exit="exit"
            transition={transition}
            className="max-w-2xl mx-auto space-y-8"
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-serif">Baseline Measurements</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <BaselineInputs
                  path={state.path as "orthopedic" | "gi"}
                  values={state.baseline}
                  onChange={handleBaselineChange}
                />
                <div className="flex gap-3 pt-4">
                  <Button onClick={goBack} variant="outline">
                    <ArrowLeft className="mr-2 w-4 h-4" />
                    Back
                  </Button>
                  <Button
                    onClick={() => goToStep(7)}
                    className="flex-1 bg-teal-700 hover:bg-teal-800 text-white border-teal-800"
                  >
                    Continue
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        );

      // ── 7 · Summary ───────────────────────────────────────────────────────────
      case 7:
        return (
          <motion.div
            key="step-7"
            variants={variants} custom={1}
            initial="enter" animate="center" exit="exit"
            transition={transition}
            className="max-w-2xl mx-auto space-y-8"
          >
            <SummaryCard
              path={state.path}
              selectedRegions={state.selectedRegions}
              regionAnswers={state.regionAnswers}
              giAnswers={state.giAnswers}
              otherConcern={state.otherConcern}
              baseline={state.baseline}
              baselineScore={calculateBaselineScore()}
              onProceed={() => goToStep(8)}
              onReview={() => goToStep(1)}
            />
          </motion.div>
        );

      // ── 8 · Confirm ───────────────────────────────────────────────────────────
      case 8:
        return (
          <motion.div
            key="step-8"
            variants={variants} custom={1}
            initial="enter" animate="center" exit="exit"
            transition={transition}
            className="max-w-2xl mx-auto space-y-8"
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-serif">Ready to start your recovery plan?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-muted-foreground leading-relaxed">
                  You're about to enter the AgelessRx recovery portal experience as a conceptual
                  preview.
                </p>
                <div className="flex flex-col gap-3 pt-4">
                  <Button
                    onClick={() => setState((prev) => ({ ...prev, confirmed: true, step: 9 }))}
                    className="bg-teal-700 hover:bg-teal-800 text-white border-teal-800"
                    data-testid="button-confirm-start"
                  >
                    Confirm & Continue
                  </Button>
                  <Button
                    onClick={() => goToStep(7)}
                    variant="outline"
                    data-testid="button-go-back-confirm"
                  >
                    <ArrowLeft className="mr-2 w-4 h-4" />
                    Go back and change answers
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        );

      // ── 9 · Portal Preview ────────────────────────────────────────────────────
      case 9:
        return (
          <motion.div
            key="step-9"
            variants={variants} custom={1}
            initial="enter" animate="center" exit="exit"
            transition={transition}
            className="max-w-3xl mx-auto"
          >
            <PortalPreview
              path={state.path}
              selectedRegions={state.selectedRegions}
              onNewSession={startOver}
            />
          </motion.div>
        );

      default:
        return null;
    }
  };

  // ── Compute display step for the progress indicator ───────────────────────────
  const { displayStep, totalSteps, stepLabel } = getDisplayStepInfo(state.step, state.path);
  const showProgress = state.step > 0 && state.step < 9;

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <Layout>
      {/* Unlisted: reachable by direct URL only, never indexed. */}
      <meta name="robots" content="noindex, nofollow" />
      <section className="container mx-auto px-6 py-12 min-h-[calc(100vh-200px)]">
        {/* Progress bar — wrapped in its own AnimatePresence so it transitions
            in sync with the content, preventing the step counter from jumping
            ahead while the previous step is still animating out. */}
        <AnimatePresence mode="wait">
          {showProgress && (
            <motion.div
              key={`progress-${displayStep}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="max-w-2xl mx-auto mb-8"
            >
              <StepProgress
                currentStep={displayStep}
                totalSteps={totalSteps}
                stepLabel={stepLabel}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {state.step > 0 && (
          <div className="max-w-2xl mx-auto mb-4 flex justify-end">
            <Button onClick={startOver} variant="ghost" size="sm">
              Start over
            </Button>
          </div>
        )}

        <AnimatePresence mode="wait">{renderStep()}</AnimatePresence>
      </section>
    </Layout>
  );
}
